import {
  Cancel,
  CheckCircle,
  Close,
  Edit,
  History,
  LocalOffer, // Icon for Discount
  LocalPrintshop,
  OpenInNew,
  Print,
  Receipt,
  RestaurantMenu,
  ShoppingBag,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { useMemo, useState } from "react";
import { formatCurrency } from "../../utils/currency";
import { useNavigate } from "react-router-dom";

// --- MOCK DATA ---
const MOCK_FULL_ORDER = {
  id: "12345",
  order_number: "ORD-2026-8842",
  status: "In Process",
  type: "Dine In",
  table: "T-04",
  area: "First Floor",
  waiter: "Rahul Sharma",
  customer: "Amit Verma",
  kots: [
    {
      kot_number: "101",
      created_at: "2026-01-17T14:35:00",
      status: "Served",
      instructions: "Serve starters immediately",
      items: [
        {
          id: 1,
          variant_name: "Paneer Tikka",
          quantity: 1,
          price: 280,
          addons: [],
          notes: ["Less Spicy"],
        },
        {
          id: 2,
          variant_name: "Masala Papad",
          quantity: 2,
          price: 60,
          addons: [{ name: "Cheese", price: 20, quantity: 2 }],
          notes: [],
        },
      ],
    },
    {
      kot_number: "102",
      created_at: "2026-01-17T14:55:00",
      status: "Cooking",
      instructions: "",
      items: [
        {
          id: 4,
          variant_name: "Paneer Butter Masala",
          quantity: 1,
          price: 320,
          addons: [{ name: "Extra Butter", price: 30, quantity: 1 }],
          notes: [],
        },
      ],
    },
  ],
};

// --- DISCOUNT MODAL COMPONENT ---
const DiscountDialog = ({ open, onClose, onApply }) => {
  const [type, setType] = useState("PERCENTAGE"); // PERCENTAGE | FLAT
  const [value, setValue] = useState("");

  const handleSubmit = () => {
    onApply({ type, value: parseFloat(value) || 0 });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Apply Discounts or Offers
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          Manual Discount
        </Typography>
        <Box display="flex" gap={1}>
          <FormControl sx={{ minWidth: 80 }}>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              size="small"
              sx={{ fontWeight: 600 }}
            >
              <MenuItem value="PERCENTAGE">%</MenuItem>
              <MenuItem value="FLAT">Flat (₹)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            placeholder="Value"
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            InputProps={{
              startAdornment:
                type === "FLAT" ? (
                  <InputAdornment position="start">₹</InputAdornment>
                ) : null,
              endAdornment:
                type === "PERCENTAGE" ? (
                  <InputAdornment position="end">%</InputAdornment>
                ) : null,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disableElevation
          sx={{ fontWeight: 600 }}
        >
          Apply Discount
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function OrderDetailsDrawer({
  isOpen,
  closeOrderDetailsDrawer,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [isDiscountOpen, setIsDiscountOpen] = useState(false);

  // State for Discount applied to this specific order
  const [discount, setDiscount] = useState({ type: null, value: 0 });

  const order = MOCK_FULL_ORDER;

  // --- LOGIC: TOTALS & DISCOUNT CALCULATION ---
  const { consolidatedItems, billDetails } = useMemo(() => {
    const map = new Map();
    let subtotal = 0;
    let count = 0;

    // 1. Consolidate Items
    order.kots.forEach((kot) => {
      kot.items.forEach((item) => {
        const itemTotal = item.price * item.quantity;
        let addonTotal = 0;
        if (item.addons) {
          item.addons.forEach(
            (a) => (addonTotal += a.price * (a.quantity || 1)),
          );
        }

        subtotal += itemTotal + addonTotal;
        count += item.quantity;

        const key = item.variant_name;
        if (!map.has(key)) {
          map.set(key, {
            ...item,
            quantity: 0,
            total_price: 0,
            all_addons: [],
          });
        }
        const existing = map.get(key);
        existing.quantity += item.quantity;
        existing.total_price += itemTotal; // Base only
        if (item.addons.length > 0) existing.all_addons.push(...item.addons);
      });
    });

    // 2. Calculate Discount Amount
    let discountAmount = 0;
    if (discount.type === "PERCENTAGE") {
      discountAmount = subtotal * (discount.value / 100);
    } else if (discount.type === "FLAT") {
      discountAmount = discount.value;
    }

    // 3. Tax Calculation (Usually on Post-Discount Amount)
    const taxableAmount = Math.max(0, subtotal - discountAmount);
    const taxAmount = taxableAmount * 0.05; // Assuming 5% GST
    const grandTotal = taxableAmount + taxAmount;

    return {
      consolidatedItems: Array.from(map.values()),
      billDetails: {
        subtotal,
        discountAmount,
        taxAmount,
        grandTotal,
        totalItemsCount: count,
      },
    };
  }, [order, discount]);

  const handleKOTAction = (action, kotNumber) => {
    console.log(`${action} triggered for KOT #${kotNumber}`);
  };

  const handleDetailClick = () => {
    navigate(`/app/order/323`);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={closeOrderDetailsDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "500px" },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* 1. HEADER */}
        <Box sx={{ p: 2, borderBottom: "1px solid #eee", bgcolor: "#fff" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight={700}>
                Order #{order.order_number.split("-").pop()}
              </Typography>
              <Chip
                label={order.type}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  height: 24,
                  borderColor: "#999",
                  color: "#555",
                }}
              />
              <Chip
                label={order.status}
                size="small"
                color={order.status === "In Process" ? "warning" : "success"}
                sx={{ fontWeight: 600, height: 24 }}
              />
            </Box>
            <IconButton size="small" onClick={closeOrderDetailsDrawer}>
              <Close />
            </IconButton>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            p={1.5}
            bgcolor="#f9fafb"
            borderRadius="8px"
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Location
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {order.table} ({order.area})
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Customer
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                {order.customer}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" color="text.secondary">
                Waiter
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                {order.waiter}
              </Typography>
            </Box>
          </Box>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            variant="fullWidth"
            sx={{ mt: 2, borderBottom: "1px solid #eee", minHeight: "40px" }}
          >
            <Tab
              icon={<History sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="KOT Summary"
              sx={{ minHeight: "40px", fontWeight: 600 }}
            />
            <Tab
              icon={<RestaurantMenu sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Items & Bill"
              sx={{ minHeight: "40px", fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* 2. CONTENT */}
        <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "#fff", p: 2 }}>
          {/* VIEW 1: KOT TIMELINE (Previous Logic) */}
          {activeTab === 0 &&
            order.kots.map((kot, index) => (
              <Box key={index} sx={{ mb: 3, position: "relative" }}>
                {index !== order.kots.length - 1 && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 14,
                      top: 35,
                      bottom: -20,
                      width: "2px",
                      bgcolor: "#eee",
                      zIndex: 0,
                    }}
                  />
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  mb={1}
                  position="relative"
                  zIndex={1}
                >
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      fontSize: 14,
                      bgcolor: "grey.800",
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <Box
                    flex={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="baseline"
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        KOT #{kot.kot_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {moment(kot.created_at).format("hh:mm A")}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color={
                        kot.status === "Cooking"
                          ? "warning.main"
                          : "success.main"
                      }
                    >
                      • {kot.status}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    ml: 5,
                    border: "1px solid #eee",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  {kot.instructions && (
                    <Box bgcolor="#fff8e1" p={1}>
                      <Typography variant="caption" color="warning.dark">
                        <b>Note:</b> {kot.instructions}
                      </Typography>
                    </Box>
                  )}
                  <List disablePadding>
                    {kot.items.map((item, i) => (
                      <ListItem
                        key={i}
                        divider={i !== kot.items.length - 1}
                        sx={{ display: "block", py: 1 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {item.quantity} x {item.variant_name}
                            </Typography>
                            {item.notes.map((note, n) => (
                              <Typography
                                key={n}
                                variant="caption"
                                display="block"
                                color="error.main"
                                fontStyle="italic"
                                sx={{ ml: 2 }}
                              >
                                * {note}
                              </Typography>
                            ))}
                          </Box>
                          <Box textAlign="right">
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {formatCurrency(item.price)} x {item.quantity}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {formatCurrency(item.price * item.quantity)}
                            </Typography>
                          </Box>
                        </Box>
                        {item.addons.map((addon, a) => (
                          <Box
                            key={a}
                            display="flex"
                            justifyContent="space-between"
                            mt={0.5}
                            pl={2}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              + {addon.name} ({addon.quantity} x{" "}
                              {formatCurrency(addon.price)})
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {formatCurrency(
                                addon.price * (addon.quantity || 1),
                              )}
                            </Typography>
                          </Box>
                        ))}
                      </ListItem>
                    ))}
                  </List>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    gap={1}
                    p={1}
                    bgcolor="#fafafa"
                    borderTop="1px solid #eee"
                  >
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Cancel sx={{ fontSize: 16 }} />}
                      onClick={() => handleKOTAction("Cancel", kot.kot_number)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<LocalPrintshop sx={{ fontSize: 16 }} />}
                      onClick={() => handleKOTAction("Print", kot.kot_number)}
                    >
                      Print
                    </Button>
                    <Button
                      size="small"
                      color="success"
                      variant="outlined"
                      startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                      onClick={() => handleKOTAction("Ready", kot.kot_number)}
                    >
                      Ready
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}

          {/* VIEW 2: ITEMS BILLING & DISCOUNT */}
          {activeTab === 1 && (
            <>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={2}
                p={1.5}
                bgcolor="#e3f2fd"
                borderRadius="8px"
              >
                <ShoppingBag color="primary" sx={{ fontSize: 20 }} />
                <Typography
                  variant="body2"
                  color="primary.dark"
                  fontWeight={600}
                >
                  Total Items: {billDetails.totalItemsCount}
                </Typography>
              </Box>

              <List
                disablePadding
                sx={{ border: "1px solid #eee", borderRadius: "8px" }}
              >
                {consolidatedItems.map((item, idx) => (
                  <ListItem
                    key={idx}
                    divider={idx !== consolidatedItems.length - 1}
                    sx={{ display: "block" }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {item.quantity} x {item.variant_name}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="body2" fontWeight={700}>
                          {formatCurrency(item.total_price)}
                        </Typography>
                      </Box>
                    </Box>
                    {item.all_addons &&
                      item.all_addons.map((addon, ax) => (
                        <Box
                          key={ax}
                          display="flex"
                          justifyContent="space-between"
                          mt={0.5}
                          pl={2}
                        >
                          <Typography variant="caption" color="text.secondary">
                            + {addon.name} ({addon.quantity} x{" "}
                            {formatCurrency(addon.price)})
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {formatCurrency(
                              addon.price * (addon.quantity || 1),
                            )}
                          </Typography>
                        </Box>
                      ))}
                  </ListItem>
                ))}
              </List>

              {/* --- BILL SUMMARY BLOCK --- */}
              <Box
                mt={3}
                p={2}
                bgcolor="#fafafa"
                borderRadius="8px"
                border="1px solid #eee"
              >
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Subtotal</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(billDetails.subtotal)}
                  </Typography>
                </Box>

                {/* DISCOUNT ROW (Dynamic) */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">Discount</Typography>
                    {billDetails.discountAmount > 0 ? (
                      <Chip
                        label={`${discount.type === "PERCENTAGE" ? discount.value + "%" : "Flat"} Applied`}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "10px", fontWeight: 700 }}
                        onDelete={() => setDiscount({ type: null, value: 0 })} // Remove Discount
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{
                          cursor: "pointer",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => setIsDiscountOpen(true)}
                      >
                        + Apply Discount
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      billDetails.discountAmount > 0
                        ? "success.main"
                        : "text.primary"
                    }
                  >
                    {billDetails.discountAmount > 0
                      ? `- ${formatCurrency(billDetails.discountAmount)}`
                      : "₹0.00"}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Tax (5%)</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(billDetails.taxAmount)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={700}>
                    Grand Total
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.main"
                  >
                    {formatCurrency(billDetails.grandTotal)}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* 3. FOOTER */}
        <Box
          p={2}
          bgcolor="#fff"
          borderTop="1px solid #eee"
          display="flex"
          alignItems="center"
          gap={2}
          boxShadow="0px -4px 10px rgba(0,0,0,0.05)"
        >
          <Box display="flex" gap={1}>
            <Tooltip title="View Details">
              <Button
                variant="outlined"
                sx={{
                  minWidth: "40px",
                  borderColor: "#ddd",
                  color: "text.secondary",
                }}
                onClick={handleDetailClick}
              >
                <OpenInNew />
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              color="inherit"
              sx={{ borderColor: "#ddd" }}
            >
              Modify
            </Button>
            <Button
              variant="outlined"
              startIcon={<Print />}
              color="inherit"
              sx={{ borderColor: "#ddd" }}
            >
              Print
            </Button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<Receipt />}
            sx={{ flex: 1, fontWeight: 700 }}
          >
            Settle {formatCurrency(billDetails.grandTotal)}
          </Button>
        </Box>
      </Drawer>

      {/* DISCOUNT DIALOG */}
      <DiscountDialog
        open={isDiscountOpen}
        onClose={() => setIsDiscountOpen(false)}
        onApply={setDiscount}
      />
    </>
  );
}
